import { Layer } from "./Layer.js";
import { roundNumber, saveAsFile } from "../../utils/utils.js";
import { Window } from "../../ui/Window.js";
import { formatLatLng } from "../popup/formattingUtils.js";
import { FeatureLayerUtils } from "./utils/FeatureLayerUtils.js";

export { GeoJSONLayer }

/**
 * GeoJSON Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.map.layer
 */
class GeoJSONLayer extends Layer {

    static color = "#00AFB9";

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // throw error if there is no GeoJSON
        if (typeof this.config.geoJSON == "string") {
            const url = this.config.geoJSON;
            fetch(url).then(response => response.json()).then(json => {
                this.config.geoJSON = json;
                if (this.activeMap_ == "leaflet") {
                    this.mapLayers_.leaflet.clearLayers();
                    this.mapLayers_.leaflet.addData(this.geoJSON);
                }
            })
            this.config.geoJSON = null;
        } else if (typeof this.config.geoJSON != "object") {
            throw new Error("Invalid or missing GeoJSON");
        }

        this.addDefaults_({
            geoJSON: null,
            // overwrite defaults
            opacity: 0.75,
            opacityRatio: 0.75,
            filterDisabled: true,
            metadataTemplate: "#geojson_layer",
            availableCrs: FeatureLayerUtils.availableCrs,
            icon: "fas fa-draw-polygon",
            // settings
            opacitySettings: true,
            editingEnabled: true
        });

        this.setAll_("type", "geojson");

        this.events["click"] = [];

        // init the event callbacks for the buttons in the popup
        if (this.editingEnabled) this.initPopupEvents_();

        // storing an array of clicked features for getFeatureInfo
        this.clickedFeatures_ = [];
    }

    /**
     * Create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type) {
        switch (type) {
            case "leaflet":
                const layer = L.geoJSON(null, {
                    pointToLayer: (geoJsonPoint, latlng) => {
                        return L.circleMarker(latlng, { renderer: FeatureLayerUtils.getCanvasRenderer() });
                    },
                    onEachFeature: (feature, layer) => {
                        if ((layer instanceof L.Path) || (layer instanceof L.Marker)) {
                            GeoJSONLayer.bindLeafletTooltip_(layer);
                            GeoJSONLayer.computeLeafletSize_(layer);
                            layer.on("click", e => {
                                this.clickedFeatures_.push(layer.feature);
                                this.fire("click", {
                                    layer: this,
                                    feature: layer.feature,
                                    mapLayer: layer
                                });
                            });
                        }
                    },
                    style: (feature) => {
                        if (!("properties" in feature)) feature.properties = {};
                        const style = {
                            opacity: this.opacity,
                            fillOpacity: this.opacity * this.opacityRatio,
                            renderer: FeatureLayerUtils.getCanvasRenderer(),
                            weight: 2
                        }
                        if ("Color" in feature.properties) {
                            style.color = feature.properties.Color;
                        } else {
                            feature.properties.Color = "";
                        }
                        return style;
                    }
                })
                this.mapLayers_[type] = layer;
                return layer;
        }
        return null;
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        const previousType = this.activeMap_;
        this.activeMap_ = type;

        switch (type) {
            case "leaflet":
                if (previousType != type) {
                    this.mapLayers_.leaflet.clearLayers();
                    if (this.geoJSON) this.mapLayers_.leaflet.addData(this.geoJSON);
                } else if (Number.isFinite(this.opacity)) {
                    // call setters to apply options to the active mapLayer if not newly created
                    this.setOpacity(this.opacity);
                }
                break;
        }
    }

    applyGeoJSONFromLayer_() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.geoJSON = this.mapLayers_.leaflet.toGeoJSON();
                if (!("properties" in this.geoJSON)) this.geoJSON.properties = {};
                this.geoJSON.properties.title = this.title;
        }
    }

    /**
     * Set label of a feature
     * @param {object} feature 
     * @param {string} label 
     */
    setLabel(feature, label) {
        feature.properties.Label = label;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.eachLayer(layer => {
                    if (layer.feature === feature) {
                        GeoJSONLayer.bindLeafletTooltip_(layer);
                    }
                });
                break;
        }
    }

    /**
     * Set color of a feature
     * @param {object} feature 
     * @param {string} color 
     */
    setColor(feature, color) {
        feature.properties.Color = color;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.eachLayer(layer => {
                    if (layer.feature === feature) {
                        layer.setStyle({ color: color });
                    }
                });
                break;
        }
    }

    /** 
     * init the event callbacks for the buttons in the popup
     * @private
     */
    initPopupEvents_() {
        let editing = false;
        this.popupEvents_ = [
            {
                title: "<i class='fas fa-edit'></i><br/>edit shape",
                callback: e => {
                    if (editing) {
                        this.stopEditing_(e.content.feature);
                    } else {
                        this.startEditing_(e.content.feature);
                    }
                    editing = !editing;
                },
                toggleMode: true,
                isEnabled: () => { return editing },
                width: "33%"
            },
            {
                title: "<i class='fas fa-download'></i><br/>export",
                callback: e => this.exportGeometry("download"),
                width: "34%"
            },
            {
                title: "<i class='fas fa-trash'></i><br/>remove",
                callback: e => this.fire("remove", this),
                width: "33%"
            },
            /*
            {
                title: "<i class='fas fa-tag'></i> set label",
                callback: e => {
                    const currentLabel = ("label" in e.content.feature.properties) ? e.content.feature.properties.label : null;
                    openLabelSelection(currentLabel, label => this.setLabel(e.content.feature, label));
                }
            }*/
        ];
    }

    /**
     * start editing for a vector based layer
     * @private
     */
    startEditing_(feature) {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.eachLayer(layer => {
                    if ((layer.feature === feature) && layer.editing) {
                        if (layer._tooltip) layer.unbindTooltip();
                        layer.editing.enable();

                        // allow opening a popup when editing the circlemarker
                        if (layer.editing._moveMarker) {
                            layer.editing._moveMarker.on("click", (e) => {
                                this.clickedFeatures_.push(layer.feature);
                                layer.editing._map.fire("click", e);
                            });
                        }
                    }
                });
                break;
        }
    }

    /**
     * stop editing for a vector based layer
     * @private
     */
    stopEditing_(feature) {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.eachLayer(layer => {
                    if ((layer.feature === feature) && layer.editing) {
                        if (layer.editing._moveMarker) layer.editing._moveMarker.off("click");
                        layer.editing.disable();
                        // workaround for updating layer._bounds after editing
                        if ("setLatLngs" in layer) {
                            layer.setLatLngs(layer._latlngs);
                        } else if ("setLatLng" in layer) {
                            layer.setLatLng(layer._latlng);
                        }
                        GeoJSONLayer.bindLeafletTooltip_(layer);
                        GeoJSONLayer.computeLeafletSize_(layer);
                    }
                });
                break;
        }

        this.applyGeoJSONFromLayer_();
    }

    /**
     * Trigger a file download containing the geometry as a geojson file
     * 
     * @param {string} mode supported values: "string", "object", "download" (default)
     */
    exportGeometry(mode) {
        // only export first feature, if only one feature exists
        let geoJSON = this.geoJSON;
        if (("features" in geoJSON) && (geoJSON.features.length == 1)) geoJSON = geoJSON.features[0];

        if (mode == "object") return this.geoJSON;

        const jsonString = JSON.stringify(this.geoJSON);
        if (mode == "string") return jsonString

        const blob = new Blob([jsonString], { type: "text/plain;charset=utf-8" });
        saveAsFile(blob, this.title + ".json");
    }

    /**
     * Get Info of the last clicked features
     * @returns {Promise} feature info
     */
    getFeatureInfo() {
        return FeatureLayerUtils.getFeatureInfo(this);
    }

    /**
     * Set the transparency between 0 and 1
     * @param {number} opacity
     */
    setOpacity(opacity) {
        FeatureLayerUtils.setOpacity(this, opacity);
    }

    /**
     * Get the bounding box as an object
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        const bounds = FeatureLayerUtils.computeBounds(this);
        return (bounds) ? bounds : super.getBounds();
    }

    /**
     * static method for computing the size of each feature and add
     * it to the feature properties
     * @param {L.Layer} layer
     * @private
     */
    static computeLeafletSize_(layer) {
        if (!("feature" in layer)) return;

        if (!(layer instanceof L.CircleMarker)) {
            // bounding box
            const bbox = layer.getBounds();
            const nw = bbox.getNorthWest();
            const se = bbox.getSouthEast();

            layer.feature.properties["North West"] = formatLatLng(nw.lat, nw.lng, "degreeminutes");
            layer.feature.properties["South East"] = formatLatLng(se.lat, se.lng, "degreeminutes");
        } else {
            // circle merker position
            const lat = layer._latlng.lat;
            const lng = layer._latlng.lng;
            layer.feature.properties["Coordinates"] = formatLatLng(lat, lng, "degreeminutes");
        }

        if ((layer instanceof L.Polyline) && !(layer instanceof L.Polygon)) {
            // measure distance
            let distance = 0;
            const latLngs = layer.getLatLngs();
            for (let i = 1; i < latLngs.length; ++i) {
                // check nesting for MultiPolyline
                if (Array.isArray(latLngs[i])) {
                    const child = latLngs[i];
                    for (let j = 1; j < child.length; ++j) {
                        distance += child[j - 1].distanceTo(child[j]);
                    }
                } else {
                    distance += latLngs[i - 1].distanceTo(latLngs[i]);
                }
            }
            layer.feature.properties["Distance"] = roundNumber(distance / 1000, 3) + " km";
        } else if (layer instanceof L.Polygon) {
            // measure area
            const geodesicArea = L.GeometryUtil.geodesicArea(layer.getLatLngs()[0]);
            let readableArea = L.GeometryUtil.readableArea(geodesicArea, true, 3);
            if (readableArea.endsWith("ha")) {
                readableArea = roundNumber(parseFloat(readableArea) / 100, 3) + " km²";
            }
            layer.feature.properties["Geodesic Area"] = readableArea;
        }
    }

    /**
     * static method to apply leaflet tooltips from the feature.label
     * @private
     * @param {L.Layer} layer 
     */
    static bindLeafletTooltip_(layer) {
        if (layer._tooltip) layer.unbindTooltip();
        if (("Label" in layer.feature.properties) && (typeof layer.feature.properties.Label == "string") && (layer.feature.properties.Label.length > 0)) {
            layer.bindTooltip(layer.feature.properties.Label, {
                permanent: true,
                opacity: 1,
                className: "leaflet-tooltip-label"
            });
        }
    }

}

// private static functions and variables

let colorPicker = null;
let labelSelector = null;

/**
 * method to generate an instance of an Iro color picker
 * inside a vef window
 * @private
 */
function createColorPicker() {

    const div = document.createElement("div");
    div.classList.add("geojson-color-picker");

    div.insertAdjacentHTML("afterbegin", `
        <div class="iro-color-picker"></div>
        <div class="color-picker-form">
            <span>R</span><input spellcheck="false" type="text" class="color-picker-form-r"/><br/>
            <span>G</span><input spellcheck="false" type="text" class="color-picker-form-g"/><br/>
            <span>B</span><input spellcheck="false" type="text" class="color-picker-form-b"/><br/>
            <span>HEX</span><input spellcheck="false" type="text" class="color-picker-form-hex"/>
            <br/><br/>
            <button class="color-submit">Apply</button>
        </div>
    `);

    const options = {
        title: "SET GEOMETRY COLOR",
        width: "351px",
        open: false,
        draggable: true,
        content: div
    };

    const vefWindow = new Window(null, options);

    const coolorPickerDiv = div.querySelector(".iro-color-picker");
    const colorPicker = new iro.ColorPicker(coolorPickerDiv, {
        // color picker options
        width: 120,
        color: GeoJSONLayer.color,
        borderWidth: 1,
        borderColor: "#fff",
        layoutDirection: "horizontal",
        layout: [
            {
                component: iro.ui.Wheel,
                options: {}
            },
            {
                component: iro.ui.Slider,
                options: {
                    sliderType: 'value',
                    sliderSize: 10
                }
            },
            {
                component: iro.ui.Slider,
                options: {
                    sliderType: 'alpha',
                    sliderSize: 10
                }
            }
        ]
    });

    const response = {
        vefWindow: vefWindow,
        colorPicker: colorPicker,
        onSubmit: null
    };

    // color channel input elements
    const colorChannels = { "r": null, "g": null, "b": null };
    for (let i in colorChannels) {
        colorChannels[i] = div.querySelector(".color-picker-form-" + i);
        colorChannels[i].addEventListener("change", () => {
            colorPicker.color.setChannel('rgb', i, colorChannels[i].value);
        });
    }

    const hex = div.querySelector(".color-picker-form-hex");
    hex.addEventListener("change", () => {
        colorPicker.color.set(hex.value);
    });

    colorPicker.on(["color:init", "color:change"], (color) => {
        colorChannels["r"].value = color.rgb.r;
        colorChannels["g"].value = color.rgb.g;
        colorChannels["b"].value = color.rgb.b;
        hex.value = color.hexString;
    });

    const submit = div.querySelector(".color-submit");
    submit.addEventListener('click', () => {
        if (typeof response.onSubmit == "function") {
            response.onSubmit(colorPicker.color);
        }
    });

    return response;
}

/**
 * open the color picker bound to a specific layer
 * @param {L.Path} layer 
 * @private
 */
export function openColorPicker(callback) {
    if (!colorPicker) colorPicker = createColorPicker();

    colorPicker.vefWindow.setOptions({
        open: true,
        center: true
    });

    colorPicker.onSubmit = (color) => {
        const hexString = color.hexString;
        GeoJSONLayer.color = hexString;
        callback(hexString);
        colorPicker.vefWindow.close();
    }
}

/**
 * open a window to pick a label
 * @param {string} currentLabel 
 * @param {function} callback 
 * @private
 */
export function openLabelSelection(currentLabel, callback) {
    if (!labelSelector) {

        // initialize if not defined
        const html = `
            <input spellcheck="false" style="width: 100%; padding: 5px" placeholder="input label text ..." class="label-input"/>
            <div style="text-align:right; margin-top:10px;">
                <button class="btn-remove-label">Remove Label</button>
                <button class="btn-apply-label">Apply</button>
            </div>
        `;

        const options = {
            title: "SET LABEL",
            width: "340px",
            open: false,
            draggable: true,
            content: html
        };

        labelSelector = new Window(null, options);
    }

    // reset UI state

    const element = labelSelector.getElement();
    const input = element.querySelector("input");
    const remove = element.querySelector(".btn-remove-label");
    const apply = element.querySelector(".btn-apply-label");

    input.value = currentLabel || "";

    remove.onclick = e => {
        labelSelector.close();
        callback(null);
    };

    const save = () => {
        labelSelector.close();
        callback(input.value.trim());
    }

    apply.onclick = save;
    input.onkeypress = e => {
        // save on pressing enter
        if (e.keyCode === 13) save();
    };

    labelSelector.setOptions({
        open: true,
        center: true
    });

    input.focus();
}